import type { Settings } from "@cuekards/core";
import {
  DEFAULT_OVERLAY,
  sanitiseOverlayDefaults,
} from "@cuekards/core/overlay-defaults";
import type { OverlayDefaults } from "@cuekards/core/overlay-defaults";
import type { SettingsOverride } from "../src/settings-bridge.js";

/**
 * A rig in the page URL (story 60), and the second of the two mitigations for
 * settings being per browser profile rather than per person.
 *
 * It is the quietly good one. A file has to be kept somewhere and remembered
 * about; a URL is a bookmark, and for a "set up once, film weekly" workflow that
 * is arguably better than persistence — it survives clearing site data, it
 * survives moving machines, and it is shareable, which nothing stored in a
 * browser is.
 *
 * **Two rules shape everything here.**
 *
 * *The parameters are the product's vocabulary, not the storage shape.* A URL is
 * a thing people read and edit, so it says `font=64`, not
 * `overlayDefaults.fontSize=64`. What each one means in the blob is this
 * module's business — see {@link applySetup}, where one parameter deliberately
 * writes two fields.
 *
 * *A setup overrides; it does not save.* The values a URL names are in force for
 * as long as you are on that URL, and this browser's own stored values for them
 * are left exactly as they were — see {@link restoreSetup}. A link is somebody
 * else's as often as your own, and one that silently rewrote the recipient's
 * saved rig would be a booby trap they could neither see nor undo. Keeping a
 * setup is what the settings *file* is for, and importing one is a deliberate
 * act.
 *
 * It shares the URL with `./shared-link.ts`, which owns `doc`. Neither writer
 * touches the other's parameters, which is what lets one link carry a script and
 * a rig at once.
 */

/**
 * What a URL can say about a rig.
 *
 * Four values, and the omissions are as considered as the inclusions. **Device
 * choices are not here**: a `deviceId` is minted per origin and per browser
 * profile, so one carried to another machine names nothing and the picker would
 * fall back to the system default anyway — a parameter that cannot work on the
 * machine it was carried to is worse than no parameter. The last loaded script
 * is not here either; that is `doc`, and it is `./shared-link.ts`'s.
 */
export interface Setup {
  /** Prompter font size in px. */
  fontSize?: number;
  /** Constant-speed scroll rate in words per minute. */
  wpm?: number;
  /** Whether voice commands are listened for. */
  voiceCommands?: boolean;
  /** Rebound shortcuts, as command → chord. */
  shortcuts?: Record<string, string>;
}

/**
 * The parameter each field is spelled with. What to call one on screen is
 * `./settings-copy.ts`'s, which is where the copy in this package lives.
 *
 * **`followVoice` is deliberately not one of these** (flow ticket 06), and this
 * is the note for whoever comes to add it. A setup is *presentation* — how big
 * the prompt is, how fast it crawls, which keys move it — and ADR-0013's whole
 * argument for putting one in force without saving it is that a link is somebody
 * else's as often as it is your own. Following your voice is not presentation: it
 * is a tens-of-megabytes download onto the machine of whoever opened the link,
 * and a parameter that switched it on would either start one without being asked
 * or hand somebody a prompter sitting in a mode it cannot deliver until they do.
 * Neither is a thing a URL should be able to do to a stranger. The box in the
 * green room is how it is turned on, and it asks.
 *
 * **`anchor` is not one of these either** (text-align ticket 01), and it is the
 * closer call of the two, because where the words sit *is* presentation in every
 * ordinary sense — it is a fraction of the prompter's height, it costs nothing to
 * apply, and it is stored in the same blob `font` is. What rules it out is what
 * it encodes: where the *sender's* webcam is bolted. Of everything a setup can
 * carry, it is the one value guaranteed to be wrong for whoever opens the link,
 * because the whole point of a link is that they are at a different desk. A
 * `font` too small is a link somebody adjusts; a script anchored for somebody
 * else's camera is a reader looking away from their own lens and no way to know
 * why. The `⋯` menu's row is how it is set, on the machine it is about.
 *
 * **Nor is `mirror`** (prompter-display ticket 02), for the anchor's reason
 * exactly. Mirror text is for a screen under teleprompter glass, so what it
 * encodes is the *sender's* rig, and a link is opened by somebody who does not
 * have one. Sent to a reader at an ordinary desk, it would draw their script
 * backwards. A rig is set up once, on the Settings screen of the machine that is
 * in it.
 */
export const SETUP_PARAMS = {
  fontSize: "font",
  wpm: "wpm",
  voiceCommands: "voice",
  shortcuts: "keys",
} as const satisfies Record<keyof Setup, string>;

/** Alias so the four names read as what they are at their uses below. */
const PARAMS = SETUP_PARAMS;

/**
 * The rig a URL asks for.
 *
 * Total, and lenient in one direction only: a value that is not a value is
 * dropped, and the rest of the URL still applies. The alternative — refusing the
 * whole URL over one bad parameter — would turn a typo in a bookmark into a rig
 * that silently did nothing. Ranges are not checked here at all, because the
 * settings store already sanitises everything it reads (`sanitiseWpm`,
 * `sanitiseOverlayDefaults`, `resolveBindings`), and having two opinions about
 * what a legal font size is would eventually be two different opinions.
 */
export function setupFromUrl(search: string): Setup {
  const params = new URLSearchParams(search);
  const setup: Setup = {};

  const font = number(params.get(PARAMS.fontSize));
  if (font !== undefined) setup.fontSize = font;

  const wpm = number(params.get(PARAMS.wpm));
  if (wpm !== undefined) setup.wpm = wpm;

  const voice = boolean(params.get(PARAMS.voiceCommands));
  if (voice !== undefined) setup.voiceCommands = voice;

  const keys = chords(params.get(PARAMS.shortcuts));
  if (keys !== undefined) setup.shortcuts = keys;

  return setup;
}

/**
 * The rig this page's own address asks for.
 *
 * Read wherever it is needed rather than passed down from the entry point, and
 * that is safe because the address does not change under it: `addressBarFor`
 * rewrites only `doc`, and an import navigates rather than editing the address
 * in place.
 */
export function thisPagesSetup(): Setup {
  return setupFromUrl(window.location.search);
}

/** Whether a URL said anything about settings at all. */
export function isEmptySetup(setup: Setup): boolean {
  return Object.keys(setup).length === 0;
}

/** The rig this browser is running on, as something a URL can carry. */
export function setupFromSettings(settings: Settings): Setup {
  const setup: Setup = {};
  const fontSize = settings.overlay?.fontSize ?? settings.overlayDefaults?.fontSize;
  if (fontSize !== undefined) setup.fontSize = fontSize;
  if (settings.constantSpeedWpm !== undefined) setup.wpm = settings.constantSpeedWpm;
  if (settings.voiceCommands !== undefined) {
    setup.voiceCommands = settings.voiceCommands;
  }
  if (settings.shortcuts !== undefined && Object.keys(settings.shortcuts).length > 0) {
    setup.shortcuts = settings.shortcuts;
  }
  return setup;
}

/**
 * The address that carries this rig — the thing to bookmark or send.
 *
 * Written through `URLSearchParams`, which escapes a chord's `+` properly; the
 * reader accepts the unescaped form as well, for whoever types one by hand.
 * Everything the URL already had that this module does not own is left in place,
 * `doc` most of all.
 */
export function setupUrlFor(href: string, setup: Setup): string {
  const url = new URL(href);
  set(url, PARAMS.fontSize, setup.fontSize?.toString());
  set(url, PARAMS.wpm, setup.wpm?.toString());
  set(
    url,
    PARAMS.voiceCommands,
    setup.voiceCommands === undefined ? undefined : setup.voiceCommands ? "on" : "off",
  );
  set(url, PARAMS.shortcuts, writeChords(setup.shortcuts));
  return url.toString();
}

/** The same address with no rig on it, and everything else — the script most of
 * all — untouched. Where an import goes: the file just chosen is the setup now,
 * and reloading onto a URL that still overrode it would be an import that
 * appeared to do nothing. */
export function withoutSetupParams(href: string): string {
  const url = new URL(href);
  for (const param of Object.values(SETUP_PARAMS)) url.searchParams.delete(param);
  return url.toString();
}

/**
 * The settings this session runs on: what is stored, with the URL over the top.
 *
 * One line of it is not a merge and is the reason this is a function rather than
 * a spread. **A font size in the URL sets the live prompter as well as the
 * default a session starts from**, because the live value wins over the default
 * wherever it is set (`liveFontSize`, in the core host) — so a URL that set only
 * the default would be masked by whatever this browser last stepped the font to,
 * which is precisely the state somebody following their own bookmark is in.
 *
 * The two nested objects are merged rather than replaced, because the URL names
 * one field of each and the rest of those objects is not its to clear.
 */
export function applySetup(stored: Settings, setup: Setup): Settings {
  const applied: Settings = { ...stored };

  if (setup.fontSize !== undefined) {
    applied.overlay = {
      monitors: {},
      ...stored.overlay,
      fontSize: setup.fontSize,
    };
    applied.overlayDefaults = {
      // Filled in with the shared rule rather than by hand, so a browser that
      // has never had a defaults blob gets the same built-ins the store would
      // have handed back for one.
      ...sanitiseOverlayDefaults(stored.overlayDefaults),
      fontSize: setup.fontSize,
    };
  }
  if (setup.wpm !== undefined) applied.constantSpeedWpm = setup.wpm;
  if (setup.voiceCommands !== undefined) applied.voiceCommands = setup.voiceCommands;
  if (setup.shortcuts !== undefined) applied.shortcuts = setup.shortcuts;

  return applied;
}

/**
 * What to store, given what the worker wrote and what this browser had.
 *
 * Everything the URL owns goes back to the stored value — including going back
 * to *absent*, which is what a key this browser never had must remain. Everything
 * else is written through, so a setup in force does not stop the last-loaded
 * script or a device choice being remembered.
 *
 * This is the rule that makes "override, not save" true of every path rather
 * than only of the obvious one. The path that is not obvious is the voice
 * switch: its truth lives in the recognition worker, so the value seeded from
 * the URL travels core → recogniser → core and comes back as a *save*, which
 * this is what stops from landing in storage.
 *
 * **A font size in the URL puts back the two objects it lives in, whole**, and
 * that is a deliberately blunt instrument rather than an oversight. Those
 * objects hold more than a font size — per-monitor geometry, the default width
 * and opacity — so restoring them wholesale discards any *other* change made to
 * them while the setup was in force. The alternative is worse in this host, and
 * the reason is that `applySetup` had to fill one of them in: putting back only
 * the font size would leave a whole `overlayDefaults` blob in a browser that
 * never had one, which is precisely the mark a setup is not supposed to leave.
 *
 * **The fields a tab has a control for are the exception**, and they are named
 * in {@link CARRIED_DEFAULTS}. A value set from one of those controls while a
 * `?font=` bookmark is open is carried across rather than restored, because
 * otherwise it would be thrown away on the next write with nothing on screen to
 * explain it. The backdrop was the first (web-ui ticket 08), when this paragraph
 * still said that nothing in a tab wrote the fields at risk. Text align was the
 * second (text-align ticket 01): the `⋯` menu writes `anchor`, and until
 * prompter-display ticket 01 a press of it under a bookmark was silently undone.
 * Mirror text was the third (prompter-display ticket 02), and Line spacing the
 * fourth (prompter-display ticket 04).
 *
 * Everything else in the object is named in {@link RESTORED_DEFAULTS} and still
 * goes back wholesale. A test fails when a field is in neither list, so a new
 * field is a decision about which list it belongs in rather than a quiet
 * addition to the wholesale restore.
 */
export function restoreSetup(
  written: Settings,
  stored: Settings,
  setup: Setup,
): Settings {
  const kept: Settings = { ...written };

  if (setup.fontSize !== undefined) {
    assign(kept, "overlay", stored.overlay);
    assign(
      kept,
      "overlayDefaults",
      carryTabControls(stored.overlayDefaults, written.overlayDefaults),
    );
  }
  if (setup.wpm !== undefined) assign(kept, "constantSpeedWpm", stored.constantSpeedWpm);
  if (setup.voiceCommands !== undefined) {
    assign(kept, "voiceCommands", stored.voiceCommands);
  }
  if (setup.shortcuts !== undefined) assign(kept, "shortcuts", stored.shortcuts);

  return kept;
}

/**
 * A setup, as the thing the settings bridge takes.
 *
 * One function rather than the two-method object written out at each of its
 * uses, because the two halves have to be the same setup and nothing but this
 * would say so — a `seed` from the address with a `keep` from somewhere else
 * would apply a rig and then save it, which is the whole failure ADR-0013 is
 * about.
 */
export function setupOverride(setup: Setup): SettingsOverride {
  return {
    seed: (stored) => applySetup(stored, setup),
    keep: (written, stored) => restoreSetup(written, stored, setup),
  };
}

/**
 * The stored defaults a tab has a control for, which a `?font=` setup carries
 * across rather than putting back (see {@link restoreSetup}).
 *
 * - `backdrop`: the prompter's scrim slider (web-ui ticket 08).
 * - `anchor`: Text align, in the `⋯` menu (text-align ticket 01).
 * - `mirror`: Mirror text, on the Settings screen (prompter-display ticket 02).
 *   The first that is not a number; the carry compares with `===`, which is
 *   exactly as true of a boolean.
 * - `lineSpacing`: Line spacing, on the Settings screen (prompter-display ticket
 *   04). Carried even though a reset puts it back: which list a field is in is
 *   whether a tab has a control for it, and this one has a slider.
 *
 * **`side` is not here, and that is deliberate.** It is where a sideways phone's
 * column sits, and only the phone has a control for it (text-align ticket 02).
 * Nothing in a tab writes it, so there is nothing to carry, and it is restored
 * with the rest.
 */
export const CARRIED_DEFAULTS = [
  "backdrop",
  "anchor",
  "mirror",
  "lineSpacing",
] as const satisfies readonly (keyof OverlayDefaults)[];

/**
 * The stored defaults a `?font=` setup puts back as this browser had them.
 *
 * The font size because it is the field the setup names. The rest because they
 * describe a window floating over other applications (width, height, opacity,
 * the two fractions) or a phone on its side (`side`), and a tab has no control
 * for any of them.
 */
export const RESTORED_DEFAULTS = [
  "width",
  "height",
  "opacity",
  "fontSize",
  "leftFraction",
  "topFraction",
  "side",
] as const satisfies readonly (keyof OverlayDefaults)[];

type CarriedField = (typeof CARRIED_DEFAULTS)[number];

/**
 * The stored defaults with the {@link CARRIED_DEFAULTS} that were actually
 * written kept on them.
 *
 * The two ways this returns nothing are the same answer to different questions,
 * and both matter. **Nothing stored and nothing written** is a browser that has
 * never had a defaults blob and did not gain one — the key stays absent, which
 * is the mark a setup is not supposed to leave. **Nothing stored but something
 * written** is that browser having had one of the tab's controls moved: the
 * blob is worth creating for that, because otherwise the tab's own controls over
 * this object are the ones that silently cannot be used from a bookmark. A
 * written blob whose carried fields are all still the built-ins has moved none
 * of them, and leaves nothing.
 */
function carryTabControls(
  stored: OverlayDefaults | undefined,
  written: OverlayDefaults | undefined,
): OverlayDefaults | undefined {
  if (written === undefined) return stored;
  const carried = Object.fromEntries(
    CARRIED_DEFAULTS.map((field) => [field, written[field]]),
  ) as Pick<OverlayDefaults, CarriedField>;
  if (stored === undefined) {
    // Filled in with the shared rule rather than by hand, for `applySetup`'s
    // reason: what is kept is a complete, in-range blob or none at all.
    return CARRIED_DEFAULTS.every((field) => written[field] === DEFAULT_OVERLAY[field])
      ? undefined
      : { ...sanitiseOverlayDefaults(undefined), ...carried };
  }
  return { ...sanitiseOverlayDefaults(stored), ...carried };
}

/** Set a parameter, or remove it where there is nothing to say. */
function set(url: URL, param: string, value: string | undefined): void {
  if (value === undefined || value === "") url.searchParams.delete(param);
  else url.searchParams.set(param, value);
}

/** Copy a value across, or remove the key entirely where there was none. */
function assign<K extends keyof Settings>(
  target: Settings,
  key: K,
  value: Settings[K],
): void {
  if (value === undefined) delete target[key];
  else target[key] = value;
}

/** A finite number, or nothing. `Number("")` is 0 and `Number(" ")` is 0, which
 * is why an empty parameter is dropped before it is parsed. */
function number(raw: string | null): number | undefined {
  if (raw === null || raw.trim() === "") return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

/**
 * A switch, in any of the spellings somebody would reasonably type.
 *
 * Anything else is *nothing* rather than false, which is the direction every
 * doubt about voice commands falls in: an unreadable value must not be able to
 * turn the microphone into something that takes orders, and it must not be able
 * to turn a rig's deliberate `off` into a silent nothing either.
 */
function boolean(raw: string | null): boolean | undefined {
  const value = raw?.trim().toLowerCase();
  if (value === "on" || value === "1" || value === "true" || value === "yes") {
    return true;
  }
  if (value === "off" || value === "0" || value === "false" || value === "no") {
    return false;
  }
  return undefined;
}

/**
 * Rebound keys, as `command:Chord,command:Chord`.
 *
 * Handed on unvalidated, because `resolveBindings` in the core is the one place
 * that decides what is a command, what is a usable key and what the browser has
 * reserved — and a URL is no more trustworthy than the settings blob it already
 * reads with exactly those doubts.
 *
 * A space is read as a `+`: a query string decodes `+` to a space, so a chord
 * typed straight into an address bar arrives with the modifier separator gone.
 */
function chords(raw: string | null): Record<string, string> | undefined {
  if (raw === null || raw.trim() === "") return undefined;
  const bindings: Record<string, string> = {};
  for (const pair of raw.split(",")) {
    const at = pair.indexOf(":");
    if (at <= 0) continue;
    const command = pair.slice(0, at).trim();
    const chord = pair.slice(at + 1).trim().replace(/\s+/gu, "+");
    if (command === "" || chord === "") continue;
    bindings[command] = chord;
  }
  return Object.keys(bindings).length === 0 ? undefined : bindings;
}

function writeChords(bindings: Record<string, string> | undefined): string | undefined {
  if (bindings === undefined) return undefined;
  const written = Object.entries(bindings)
    .map(([command, chord]) => `${command}:${chord}`)
    .join(",");
  return written === "" ? undefined : written;
}
